import React, { useMemo, useState, useCallback } from "react";
import Table, { TableColumn, TableProps } from "./Table";
import { cn } from "@/lib/utils";

// 그룹 키 접근자
type Accessor<T> = (row: T) => string;

export interface GroupTableProps<T = any>
  extends Omit<TableProps, "data" | "contextMenuItems" | "onContextMenu" | "showRowNumber"> {
  data: T[];
  groupBy: keyof T | Accessor<T>;
  sumColumns: string[];
  summaryLabelColumn?: string;
  getSummaryLabel?: (groupKey: string, rows: T[]) => string;

  summaryRowBg?: string;
  summaryRowText?: string;
  summaryRowBorderLeft?: string;
  summaryRowBorderBottom?: string;

  defaultCollapsed?: boolean;
}

// GroupTable: 합계행/접기·펼치기 포함 그룹 테이블
export default function GroupTable<T = any>({
  data,
  columns,
  groupBy,
  sumColumns,
  summaryLabelColumn,
  getSummaryLabel,
  summaryRowBg = "#F6FBEF",
  summaryRowText = "#2D4A1A",
  summaryRowBorderLeft = "#AAD969",
  summaryRowBorderBottom = "#AAD969",
  defaultCollapsed = false,

  // Table 공통 옵션 전달
  showCheckbox = false,
  stickyCheckbox = false,
  stickyRowNumber = false,
  highlightOnClick,
  className,
  ...rest
}: GroupTableProps<T>) {
  // 그룹 키 생성
  const makeKey = useCallback(
    (row: T) => (typeof groupBy === "function" ? groupBy(row) : String((row as any)[groupBy] ?? "")),
    [groupBy]
  );

  // 데이터 그룹핑
  const groups = useMemo(() => {
    const m = new Map<string, T[]>();
    data.forEach((r) => {
      const k = makeKey(r) || "__UNKNOWN__";
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(r);
    });
    return m;
  }, [data, makeKey]);

  // 접힘 상태 관리
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    groups.forEach((_, k) => (init[k] = defaultCollapsed));
    return init;
  });
  const toggle = (k: string) => setCollapsed((p) => ({ ...p, [k]: !p[k] }));

  // 플랫 데이터 생성(합계행 + 상세행)
  type RowEx = any & {
    __rowType?: "summary" | "detail";
    __groupKey?: string;
    __rowNoInGroup?: number;
    __detailSeq?: number;
    __detailSeqInGroup?: number;
  };
  const flatData: RowEx[] = useMemo(() => {
    const out: RowEx[] = [];
    let detailSeq = 0;

    groups.forEach((rows, key) => {
      // 합계행 기본값
      const first = rows[0] as any;
      const summary: RowEx = { __rowType: "summary", __groupKey: key };

      // 합계행 메타
      if (first) {
        if ("companyCode" in first) summary.companyCode = first.companyCode;
        if ("companyName" in first) summary.companyName = first.companyName;
      }

      // 합계행: 품목 수/라벨
      const itemCount = rows.length;
      summary.itemCode = `총 ${itemCount}개 품목`;
      if (summaryLabelColumn) summary[summaryLabelColumn] = "";

      // 합계 계산
      sumColumns.forEach((c) => {
        const sum = rows.reduce((acc, r: any) => {
          const v = r[c];
          const n =
            typeof v === "number"
              ? v
              : v == null
              ? 0
              : parseFloat(String(v).replaceAll(",", "").trim()) || 0;
          return acc + n;
        }, 0);
        summary[c] = sum;
      });

      out.push(summary);

      // 상세행 펼치기
      if (!collapsed[key]) {
        let detailSeqInGroup = 0;
        rows.forEach((r, i) => {
          detailSeq += 1;
          detailSeqInGroup += 1;
          out.push({
            ...r,
            __rowType: "detail",
            __groupKey: key,
            __rowNoInGroup: i + 1,
            __detailSeq: detailSeq,
            __detailSeqInGroup: detailSeqInGroup,
          });
        });
      }
    });
    return out;
  }, [groups, sumColumns, summaryLabelColumn, getSummaryLabel, collapsed]);

  // 번호/토글 컬럼 정의
  const numberOrToggleCol: TableColumn = {
    key: "__no",
    header: "번호",
    width: "64px",
    headerAlign: "center",
    bodyAlign: "center",
    sticky: stickyRowNumber ? "left" : undefined,
    render: (_v, row: RowEx) => {
      if (row.__rowType === "summary") {
        const isOpen = !collapsed[row.__groupKey!];
        return (
          <div className="flex items-center justify-center h-full">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggle(row.__groupKey!);
              }}
              className="flex items-center justify-center w-6 h-6 rounded hover:bg-gray-100"
              title={isOpen ? "접기" : "펼치기"}
              aria-label={isOpen ? "접기" : "펼치기"}
            >
              <img
                src="/icons/headerArrowBlack.svg"
                alt={isOpen ? "접기" : "펼치기"}
                className={cn(
                  "w-6 h-6 origin-center cursor-pointer",
                  isOpen ? "rotate-0 scale-100" : "-rotate-90 scale-95"
                )}
              />
            </button>
          </div>
        );
      }
      return row.__rowNoInGroup ?? "";
    },
  };

  // 합계행 클래스 지정
  const rowClassName = (row: RowEx) => (row.__rowType === "summary" ? "js-group-summary-row" : "");

  // 합계행 텍스트 색상 지정
  const rowTextColor = (row: RowEx) => (row.__rowType === "summary" ? summaryRowText : null);

  // 행 클릭 핸들러(요약행 토글 / 상세행 패스스루)
  const handleRowClick = (row: RowEx, index: number) => {
    if (row.__rowType === "summary") {
      toggle(row.__groupKey!);
      return;
    }
    rest.onRowClick?.(row, index);
  };

  // 합계행 스타일 오버라이드
  const styleOverride = (
    <style>{`
      .js-group-summary-row { background: ${summaryRowBg}; cursor: pointer; }
      .js-group-summary-row td {
        border-left-color: ${summaryRowBorderLeft} !important;
        border-bottom: 1px solid ${summaryRowBorderBottom} !important;
        font-weight: 700;
      }
      .js-group-summary-row .sticky-edge-left::before,
      .js-group-summary-row .sticky-edge-right::after { background: ${summaryRowBorderLeft}; }
    `}</style>
  );

  // 최종 컬럼 목록(번호/토글 포함)
  const finalColumns: TableColumn[] = useMemo(() => [numberOrToggleCol, ...columns], [columns, collapsed]);

  // 접힘 상태 시그니처(리렌더 트리거)
  const collapseSig = useMemo(() => {
    const keys = Array.from(groups.keys());
    return keys.map((k) => (collapsed[k] ? "1" : "0")).join("");
  }, [groups, collapsed]);

  // 셀 병합 규칙
  const getCellColSpan = (row: RowEx, column: TableColumn) => {
    if (row.__rowType === "summary") {
      if (column.key === "itemCode") return 2;
      if (column.key === "itemName") return 0;
    }
    return 1;
  };

  return (
    <>
      {styleOverride}
      <Table
        key={`grp-${collapseSig}`}
        {...rest}
        className={className}
        columns={finalColumns}
        data={flatData}
        showCheckbox={showCheckbox}
        stickyCheckbox={stickyCheckbox}
        showRowNumber={false}
        rowClassName={rowClassName}
        rowTextColor={rowTextColor}
        highlightOnClick={highlightOnClick}
        onRowClick={handleRowClick}
        getCellColSpan={getCellColSpan}
        getRowBgClassName={(row: RowEx) => {
          if (row.__rowType === "summary") return "js-group-summary-row";
          // 그룹 내 첫 자식(1)은 흰색, 2는 회색 …처럼 그룹별로 리셋
          return row.__detailSeqInGroup % 2 === 0 ? "bg-[#F6F6F6]" : "bg-white";
        }}
      />
    </>
  );
}
